import { useFragment } from '@apollo/client/react';

import { isMemberLoggedIn } from '@trello/authentication';
import { isWorkspaceReadOnly } from '@trello/business-logic/organization';
import { Entitlements } from '@trello/entitlements';

import { BoardCommentPermissionsFragmentDoc } from './BoardCommentPermissionsFragment.generated';
import { BoardOrganizationCommentPermissionsFragmentDoc } from './BoardOrganizationCommentPermissionsFragment.generated';
import { BoardSelfJoinFragmentDoc } from './BoardSelfJoinFragment.generated';
import { useBoardMembers } from './useBoardMembers';

interface Options {
  boardId: string;
}

interface BoardPermissions {
  canComment: (idMember: string) => boolean;
  canVote: (idMember: string) => boolean;
  canEdit: (idMember: string) => boolean;
  isTemplate: () => boolean;
}

export const useBoardPermissions = ({ boardId }: Options): BoardPermissions => {
  const {
    isAdmin,
    isMember,
    getMemberType,
    isMemberOfOrganization,
    isAdminOfOrganization,
  } = useBoardMembers(boardId);

  const { data, complete } = useFragment({
    fragment: BoardCommentPermissionsFragmentDoc,
    fragmentName: 'BoardCommentPermissions',
    from: {
      id: boardId,
      __typename: 'Board',
    },
  });

  // The codegen fragment hook returned `undefined` until the read was
  // complete; this gate preserves that and may be removable later.
  const board = complete ? data : undefined;

  // Planner events use a <CardDoneState /> component which uses allowsSelfJoin()
  // to determine whether to show editable or readonly done state. Since cards associated
  // with planner events do not have all the fields in BoardCommentPermissionsFragment, this
  // separate, limited fragment is used for this one permission check.
  const { data: selfJoinFragmentData, complete: selfJoinFragmentComplete } =
    useFragment({
      fragment: BoardSelfJoinFragmentDoc,
      fragmentName: 'BoardSelfJoin',
      from: {
        id: boardId,
        __typename: 'Board',
      },
    });

  // The codegen fragment hook returned `undefined` until the read was
  // complete; this gate preserves that and may be removable later.
  const boardSelfJoinPrefs = selfJoinFragmentComplete
    ? selfJoinFragmentData
    : undefined;

  const {
    data: orgPermissionsFragmentData,
    complete: orgPermissionsFragmentComplete,
  } = useFragment({
    fragment: BoardOrganizationCommentPermissionsFragmentDoc,
    fragmentName: 'BoardOrganizationCommentPermissions',
    from: {
      id: boardId,
      __typename: 'Board',
    },
  });

  // The codegen fragment hook returned `undefined` until the read was
  // complete; this gate preserves that and may be removable later.
  const boardOrg = orgPermissionsFragmentComplete
    ? orgPermissionsFragmentData
    : undefined;

  const offering = boardOrg?.organization?.offering;
  const status = boardOrg?.organization?.limits.orgs.usersPerFreeOrg.status;
  const isWorkspaceFree = Entitlements.isFree(offering);
  const isBoardReadOnly = isWorkspaceReadOnly(offering, status);

  const isBoardClosed = board?.closed ?? false;

  const isTemplate = (): boolean => {
    const isPublic = board?.prefs?.permissionLevel === 'public';
    if (!board?.prefs?.isTemplate) {
      return false;
    } else if (
      isPublic ||
      board?.premiumFeatures.includes('privateTemplates')
    ) {
      return true;
    }

    return false;
  };

  const allowsSelfJoin = (): boolean => {
    return (boardSelfJoinPrefs?.prefs?.selfJoin ?? false) && !isTemplate();
  };

  const memberMatchesSetting = (
    idMember: string,
    settingValue: string,
  ): boolean => {
    const isMemberObserver = getMemberType(idMember) === 'observer';
    const isMemberOfBoard = isMember(idMember);
    switch (settingValue) {
      case 'public':
        return isMemberLoggedIn();
      case 'org':
        return (
          isMemberOfBoard ||
          isMemberOfOrganization(idMember) ||
          isMemberObserver
        );
      case 'observers':
        return isMemberOfBoard || isMemberObserver;
      case 'members':
        return isMemberOfBoard;
      default:
        return false;
    }
  };

  const isEditableByWorkspaceMember = (idMember: string): boolean => {
    return (
      (!isWorkspaceFree && isAdminOfOrganization(idMember)) ||
      (allowsSelfJoin() && isMemberOfOrganization(idMember))
    );
  };

  const canComment = (idMember: string): boolean => {
    const commentingPreference = board?.prefs?.comments;
    const isCommentingEnabled = commentingPreference !== 'disabled';

    if (isBoardReadOnly || isBoardClosed) {
      //if the workspace is read only or closed, no one can comment.
      return false;
    } else if (isAdmin(idMember)) {
      return isCommentingEnabled;
    } else if (isMemberLoggedIn()) {
      return (
        memberMatchesSetting(idMember, commentingPreference ?? '') ||
        (isEditableByWorkspaceMember(idMember) && isCommentingEnabled)
      );
    } else {
      return commentingPreference === 'public';
    }
  };

  const canVote = (idMember: string): boolean => {
    const votingPreference = board?.prefs?.voting;
    const isVotingEnabled = votingPreference !== 'disabled';

    if (isBoardReadOnly || isBoardClosed) {
      //if the workspace is read only or closed, no one can vote.
      return false;
    } else if (isAdmin(idMember)) {
      return votingPreference !== 'disabled';
    } else if (isMemberLoggedIn()) {
      return (
        memberMatchesSetting(idMember, votingPreference ?? '') ||
        (isEditableByWorkspaceMember(idMember) && isVotingEnabled)
      );
    } else {
      return votingPreference === 'public';
    }
  };

  const canEdit = (idMember: string): boolean => {
    if (isBoardReadOnly || isBoardClosed) {
      return false;
    }

    const memberType = getMemberType(idMember) ?? '';

    // If a board with observers belongs to a workspace that downgrades to
    // free, we should continue to respect the read-only permissions for the
    // observers even though now they are by default admins of the (free) workspace
    if (
      memberType === 'observer' &&
      (!isAdminOfOrganization(idMember) || isWorkspaceFree)
    ) {
      return false;
    }

    if (['admin', 'normal'].includes(memberType)) {
      return true;
    }

    return isEditableByWorkspaceMember(idMember);
  };

  return {
    canComment,
    canVote,
    canEdit,
    isTemplate,
  };
};
